import { normalizeRoles, SYSTEM_ROLES, OWNER_ROLES } from '@/constants/roles';
import { 
  getOwnerRolesForOrganizationType, 
  isRoleValidForOrganizationType 
} from '@/constants/organizationRoles';

// Re-export normalizeRoles for convenience
export { normalizeRoles };

/**
 * Type definition for organization-specific roles
 */
export interface OrganizationRoles {
  [organizationId: string]: {
    systemRole: string;        // SINGLE VALUE - only one system role allowed
    ownerRoles: string[];      // ARRAY - multiple owner roles allowed
  };
}

/**
 * User interface with organization-specific roles support
 * This extends the base User type to include organizationRoles
 */
export interface UserWithOrganizationRoles {
  id: string;
  role?: string;                     // Legacy: System role (SINGLE VALUE)
  roles?: string[];                  // Legacy: Owner roles (ARRAY)
  organizationId?: string;           // Legacy: Primary organization
  organizationIds?: string[];       // All organizations user belongs to
  organizationRoles?: OrganizationRoles; // NEW: Organization-specific roles
  [key: string]: any;                // Allow other user properties
}

/**
 * Get user's system roles (user, manager, admin)
 */
export function getUserSystemRoles(userRoles: string | string[] | undefined): string[] {
  const roles = normalizeRoles(userRoles);
  return roles.filter(r => SYSTEM_ROLES.includes(r as any));
}

/**
 * Get user's owner roles (Site Manager, Safety Officer, etc.)
 * 
 * @deprecated Use getUserOwnerRolesForOrganizationType() instead for organization-type-aware role filtering
 * This function filters against all possible owner roles (backward compatibility)
 */
export function getUserOwnerRoles(userRoles: string | string[] | undefined): string[] {
  const roles = normalizeRoles(userRoles);
  return roles.filter(r => OWNER_ROLES.includes(r as any));
}

/**
 * Get user's owner roles filtered by organization type
 * Only returns roles that are valid for the specified organization type
 * 
 * @param userRoles - User's roles (string or array)
 * @param organizationType - Organization type to filter roles by
 * @returns Array of owner roles valid for the organization type
 */
export function getUserOwnerRolesForOrganizationType(
  userRoles: string | string[] | undefined,
  organizationType: string | null | undefined
): string[] {
  const roles = normalizeRoles(userRoles);
  const validRoles = getOwnerRolesForOrganizationType(organizationType);
  return roles.filter(r => validRoles.includes(r));
}

/**
 * Check if user is a regular user (only has 'user' system role)
 */
export function isSystemUser(userRoles: string | string[] | undefined): boolean {
  const systemRoles = getUserSystemRoles(userRoles);
  return systemRoles.length === 1 && systemRoles[0] === 'user';
}

/**
 * Check if user is a manager or admin
 */
export function isSystemManager(userRoles: string | string[] | undefined): boolean {
  const systemRoles = getUserSystemRoles(userRoles);
  return systemRoles.includes('manager') || systemRoles.includes('admin');
}

/**
 * Check if user has all owner roles (should see all tasks)
 */
export function hasAllOwnerRoles(userRoles: string | string[] | undefined): boolean {
  const ownerRoles = getUserOwnerRoles(userRoles);
  return OWNER_ROLES.every(role => ownerRoles.includes(role));
}

/**
 * Check if task's owner role matches user's owner roles
 * 
 * @param taskOwnerRole - The owner role assigned to the task
 * @param userOwnerRoles - Array of user's owner roles
 * @param organizationType - Optional organization type for validation
 * @returns True if task owner role matches any of user's owner roles
 */
export function taskMatchesUserOwnerRoles(
  taskOwnerRole: string | undefined,
  userOwnerRoles: string[],
  organizationType?: string | null
): boolean {
  if (!taskOwnerRole) return false;
  
  // If organization type is provided, validate that the role is valid for that org type
  if (organizationType && !isRoleValidForOrganizationType(taskOwnerRole, organizationType)) {
    // Role is not valid for this organization type, so task doesn't match
    return false;
  }
  
  return userOwnerRoles.includes(taskOwnerRole);
}

/**
 * Fetch user's roles from Firestore
 */
export async function fetchUserRoles(userId: string): Promise<{
  systemRoles: string[];
  ownerRoles: string[];
  isSystemUser: boolean;
  isSystemManager: boolean;
}> {
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('@/firebase/config');
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const userData = userSnap.data();
      const userRoles = normalizeRoles(userData?.roles || userData?.role);
      const systemRoles = getUserSystemRoles(userRoles);
      const ownerRoles = getUserOwnerRoles(userRoles);
      
      return {
        systemRoles,
        ownerRoles,
        isSystemUser: systemRoles.length === 1 && systemRoles[0] === 'user',
        isSystemManager: systemRoles.includes('manager') || systemRoles.includes('admin')
      };
    }
  } catch (error: any) {
    console.error('Error fetching user roles:', error);
    // Handle auth errors (token expiration, etc.)
    const { handleFirebaseAuthError } = await import('@/utils/firebaseErrorHandler');
    await handleFirebaseAuthError(error);
  }
  
  return {
    systemRoles: [],
    ownerRoles: [],
    isSystemUser: false,
    isSystemManager: false
  };
}

// ============================================================================
// Organization-Specific Role Helpers (Phase 1: Foundation)
// ============================================================================

/**
 * Get user's system role for a specific organization
 * Returns single system role (only one system role is allowed)
 * Falls back to global system role if organization-specific role doesn't exist
 * 
 * @param user - User object (can be UserWithOrganizationRoles or any user-like object)
 * @param organizationId - Organization ID to get roles for
 * @returns System role string or undefined if no role found
 */
export function getUserSystemRoleForOrganization(
  user: UserWithOrganizationRoles | { role?: string | string[]; organizationRoles?: OrganizationRoles | any; [key: string]: any } | null,
  organizationId: string
): string | undefined {
  if (!user) return undefined;
  
  // Priority 1: organizationRoles[organizationId].systemRole (new format)
  if (user.organizationRoles?.[organizationId]?.systemRole) {
    return user.organizationRoles[organizationId].systemRole;
  }
  
  // Priority 2: organizationRoles[organizationId].role (old nested format - backward compatibility)
  // This handles data that was migrated with the old structure
  if (user.organizationRoles?.[organizationId]?.role) {
    return user.organizationRoles[organizationId].role;
  }
  
  // Priority 3: Legacy role field (global system role)
  // Handle both string and string[] for backward compatibility
  if (user.role) {
    if (typeof user.role === 'string') {
      return user.role;
    } else if (Array.isArray(user.role)) {
      // If role is an array, find the first system role
      const systemRoles = user.role.filter(r => SYSTEM_ROLES.includes(r as any));
      return systemRoles.length > 0 ? systemRoles[0] : undefined;
    }
  }
  
  return undefined;
}

/**
 * Get user's owner roles for a specific organization
 * Falls back to global owner roles if organization-specific roles don't exist
 * 
 * @param user - User object (can be UserWithOrganizationRoles or any user-like object)
 * @param organizationId - Organization ID to get roles for
 * @returns Array of owner roles
 */
export function getUserOwnerRolesForOrganization(
  user: UserWithOrganizationRoles | { roles?: string[]; organizationRoles?: OrganizationRoles | any; [key: string]: any } | null,
  organizationId: string
): string[] {
  if (!user) return [];
  
  // Priority 1: organizationRoles[organizationId].ownerRoles (new format)
  if (user.organizationRoles?.[organizationId]?.ownerRoles) {
    return user.organizationRoles[organizationId].ownerRoles;
  }
  
  // Priority 2: organizationRoles[organizationId].roles (old nested format - backward compatibility)
  // This handles data that was migrated with the old structure
  // Filter out system roles to get only owner roles
  if (user.organizationRoles?.[organizationId]?.roles && Array.isArray(user.organizationRoles[organizationId].roles)) {
    const allRoles = user.organizationRoles[organizationId].roles;
    // Filter out system roles (user, manager, admin) to get only owner roles
    return allRoles.filter((role: string) => !SYSTEM_ROLES.includes(role as any));
  }
  
  // Priority 3: Legacy roles field (global owner roles)
  // Filter out system roles to get only owner roles
  if (user.roles && Array.isArray(user.roles)) {
    return user.roles.filter((role: string) => !SYSTEM_ROLES.includes(role as any));
  }
  
  return [];
}

/**
 * Get all roles (system + owner) for a specific organization
 * Combines system and owner roles for convenience
 * 
 * @param user - User object
 * @param organizationId - Organization ID to get roles for
 * @returns Array of all roles (systemRole + ownerRoles)
 */
export function getAllRolesForOrganization(
  user: UserWithOrganizationRoles | { role?: string; roles?: string[]; organizationRoles?: OrganizationRoles },
  organizationId: string
): string[] {
  const systemRole = getUserSystemRoleForOrganization(user, organizationId);
  const ownerRoles = getUserOwnerRolesForOrganization(user, organizationId);
  return systemRole ? [systemRole, ...ownerRoles] : ownerRoles;
}

/**
 * Check if user is admin in a specific organization
 * 
 * @param user - User object
 * @param organizationId - Organization ID to check
 * @returns True if user is admin in the organization
 */
export function isAdminInOrganization(
  user: UserWithOrganizationRoles | { role?: string | string[]; organizationRoles?: OrganizationRoles; [key: string]: any } | null,
  organizationId: string
): boolean {
  if (!user) return false;
  const systemRole = getUserSystemRoleForOrganization(user, organizationId);
  return systemRole === 'admin';
}

/**
 * Check if user is manager or admin in a specific organization
 * 
 * @param user - User object
 * @param organizationId - Organization ID to check
 * @returns True if user is manager or admin in the organization
 */
export function isManagerInOrganization(
  user: UserWithOrganizationRoles | { role?: string | string[]; organizationRoles?: OrganizationRoles; [key: string]: any } | null,
  organizationId: string
): boolean {
  if (!user) return false;
  const systemRole = getUserSystemRoleForOrganization(user, organizationId);
  return systemRole === 'manager' || systemRole === 'admin';
}

/**
 * Check if user is a regular user (only 'user' system role) in a specific organization
 * 
 * @param user - User object
 * @param organizationId - Organization ID to check
 * @returns True if user has only 'user' system role in the organization
 */
export function isSystemUserInOrganization(
  user: UserWithOrganizationRoles | { role?: string | string[]; organizationRoles?: OrganizationRoles; [key: string]: any } | null,
  organizationId: string
): boolean {
  if (!user) return false;
  const systemRole = getUserSystemRoleForOrganization(user, organizationId);
  return systemRole === 'user';
}

/**
 * Check if user is manager or admin in any of the given organizations.
 * Used to allow group creation for users who are admin/manager in any org.
 *
 * @param user - User object (with role and/or organizationRoles)
 * @param organizationIds - Organization IDs to check
 * @returns True if user is manager or admin in at least one organization
 */
export function isManagerOrAdminInAnyOrganization(
  user: UserWithOrganizationRoles | { role?: string | string[]; organizationRoles?: OrganizationRoles; [key: string]: any } | null,
  organizationIds: string[]
): boolean {
  if (!user || !organizationIds?.length) return false;
  return organizationIds.some((orgId) => isManagerInOrganization(user, orgId));
}
